import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  Bars3BottomLeftIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { AppFrame } from '../layout/AppFrame';
import { AgentStatusTrigger } from '../layout/AgentStatusTrigger';
import {
  Cluster,
  Container,
  Grid,
  Inline,
  Modal,
  ModalBody,
  ModalHeader,
  SegmentedControl,
  Stack,
  Surface,
  ToolbarActions,
} from '../ui';
import type { SegmentedOption } from '../ui';

export type TemplateEditorView = 'build' | 'preview' | 'report';

export interface TemplateEditorWorkspaceProps {
  title: string;
  metadata?: ReactNode;
  onBack: () => void;
  tools?: ReactNode;
  contextTools?: ReactNode;
  renderLifecycle?: (noticeTargetTestId?: string) => ReactNode;
  view: TemplateEditorView;
  viewOptions: Array<SegmentedOption<TemplateEditorView>>;
  onViewChange: (view: TemplateEditorView) => void;
  outline: ReactNode;
  outlineLabel: string;
  inspector: ReactNode;
  inspectorLabel: string;
  children: ReactNode;
}

/**
 * Full-screen document studio with one governed header followed by the
 * outline/canvas/properties workspace. View-specific tools remain in the
 * header instead of creating a second persistent command row.
 */
export function TemplateEditorWorkspace({
  title,
  metadata,
  onBack,
  tools,
  contextTools,
  renderLifecycle,
  view,
  viewOptions,
  onViewChange,
  outline,
  outlineLabel,
  inspector,
  inspectorLabel,
  children,
}: TemplateEditorWorkspaceProps): JSX.Element {
  const [viewPickerOpen, setViewPickerOpen] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [wideHeader, setWideHeader] = useState(() => (
    typeof window === 'undefined' || window.matchMedia('(min-width: 1280px)').matches
  ));
  const activeViewLabel = viewOptions.find((option) => option.value === view)?.label ?? 'View';

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1280px)');
    const onChange = (event: MediaQueryListEvent) => {
      setWideHeader(event.matches);
      if (event.matches) setLifecycleOpen(false);
    };
    setWideHeader(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return (
    <Stack
      gap={0}
      className="flex flex-col fixed inset-0 h-dvh overflow-hidden bg-background"
      data-testid="template-editor-workspace"
    >
      <Surface
        as="section"
        variant="flat"
        padding="none"
        className="h-header shrink-0 rounded-none border-x-0 border-t-0"
        data-testid="template-editor-header"
      >
        <Grid
          columns={{ base: 3 }}
          gap={2}
          role="navigation"
          aria-label="Template editor"
          className="h-full min-w-0 items-center px-2 sm:px-4"
        >
          <Inline gap={2} align="center" className="min-w-0 justify-start">
            <span className="hidden shrink-0 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground 2xl:inline">
              DefectSnap
            </span>
            <ToolbarActions
              size="sm"
              maxVisible={1}
              actions={[
                {
                  id: 'back',
                  label: 'Back to templates',
                  icon: <ArrowLeftIcon className="h-4 w-4" />,
                  iconOnly: true,
                  variant: 'secondary',
                  pinned: true,
                  onClick: onBack,
                  'data-testid': 'editor-back',
                },
              ]}
            />
            <Stack gap={0} className="min-w-0 flex-1">
              <h1 className="truncate text-sm font-semibold text-foreground">{title}</h1>
              {metadata && (
                <Cluster gap={1.5} align="center" className="hidden min-w-0 text-xs text-muted-foreground 2xl:flex">
                  {metadata}
                </Cluster>
              )}
            </Stack>
          </Inline>

          <Inline gap={2} align="center" className="min-w-0 justify-center">
            {/* `relative` wrapper so `contextTools` (the preview-only web/
                tablet/mobile viewport toggle, conditionally mounted on
                `tab === 'preview'`) sits OUTSIDE this row's normal flex flow
                via `absolute` below — story 2-95b resume, direct live user
                feedback: with contextTools INLINE, this whole `justify-center`
                row's rendered width changed every time it mounted/unmounted,
                so the "Editor view" switcher visibly jumped sideways on every
                Build<->Preview transition. Anchoring contextTools to this
                group's own right edge instead keeps the switcher's centered
                position identical across all three views. */}
            <Inline gap={2} align="center" className="relative min-w-0">
              <Stack gap={0} className="hidden lg:block">
                <SegmentedControl
                  aria-label="Editor view"
                  value={view}
                  onChange={onViewChange}
                  options={viewOptions}
                  size="sm"
                />
              </Stack>
              <ToolbarActions
                size="sm"
                className="hidden sm:block lg:hidden"
                maxVisible={1}
                actions={[
                  {
                    id: 'view',
                    label: String(activeViewLabel),
                    onClick: () => setViewPickerOpen(true),
                    'data-testid': 'editor-view-drawer-trigger',
                  },
                ]}
              />
              <ToolbarActions
                size="sm"
                className="sm:hidden"
                maxVisible={1}
                actions={[
                  {
                    id: 'view-mobile',
                    label: `Editor view: ${String(activeViewLabel)}`,
                    icon: <EllipsisHorizontalIcon className="h-4 w-4" />,
                    iconOnly: true,
                    onClick: () => setViewPickerOpen(true),
                    'data-testid': 'editor-view-drawer-trigger-mobile',
                  },
                ]}
              />
              {/* Undo/redo — grouped with the view switcher, matching
                  `Headers.tsx`'s own `WorkspaceHeader.tools` doc comment
                  ("Tool cluster — view-mode switch (SegmentedControl),
                  undo/redo, etc.") as ONE cluster. Previously rendered far to
                  the LEFT, beside the title, which is the finding the direct
                  live feedback named ("undo and redo buttons are far placed
                  on left"). */}
              {tools && <Stack gap={0} className="hidden shrink-0 sm:block">{tools}</Stack>}
              {contextTools && (
                <div className="absolute left-full top-1/2 ml-2 flex -translate-y-1/2 shrink-0 items-center">
                  {contextTools}
                </div>
              )}
            </Inline>
          </Inline>

          <Inline gap={1} align="center" className="min-w-0 justify-end">
            <ToolbarActions
              size="sm"
              className="md:hidden"
              actions={[
                {
                  id: 'outline',
                  label: outlineLabel,
                  icon: <Bars3BottomLeftIcon className="h-4 w-4" />,
                  iconOnly: true,
                  onClick: () => setOutlineOpen(true),
                  'data-testid': 'editor-outline-trigger',
                },
              ]}
            />
            <ToolbarActions
              size="sm"
              className="xl:hidden"
              actions={[
                {
                  id: 'inspector',
                  label: inspectorLabel,
                  icon: <AdjustmentsHorizontalIcon className="h-4 w-4" />,
                  iconOnly: true,
                  onClick: () => setInspectorOpen(true),
                  'data-testid': 'editor-inspector-trigger',
                },
              ]}
            />
            <AgentStatusTrigger labelFrom="xl" size="sm" testId="editor-agent-trigger" />
            {renderLifecycle && wideHeader && (
              <Stack gap={0}>{renderLifecycle('template-editor-lifecycle-notices')}</Stack>
            )}
            {renderLifecycle && !wideHeader && (
              <ToolbarActions
                size="sm"
                maxVisible={1}
                actions={[
                  {
                    id: 'document-actions',
                    label: 'Document actions',
                    icon: <EllipsisHorizontalIcon className="h-4 w-4" />,
                    iconOnly: true,
                    onClick: () => setLifecycleOpen(true),
                    'data-testid': 'editor-lifecycle-trigger',
                  },
                ]}
              />
            )}
          </Inline>
        </Grid>
      </Surface>

      <AppFrame
        rail={outline}
        panel={inspector}
        railWidth="var(--shell-sidebar-w)"
        panelWidth="var(--shell-sidebar-w)"
        railClassName="hidden h-full overflow-y-auto md:block"
        panelClassName="hidden h-full overflow-y-auto xl:block"
        className="min-h-0 flex-1 overflow-hidden"
        padded={false}
        contentMaxWidth="var(--content-fluid)"
        data-testid="template-editor-frame"
      >
        <Container
          as="main"
          width="fluid"
          className="h-full min-w-0 overflow-y-auto bg-muted/20 px-3 py-5 sm:px-5 lg:px-8"
          data-testid="template-editor-canvas"
        >
          <Stack gap={0}>
            <Stack
              gap={1}
              className="mb-3 empty:hidden"
              data-testid="template-editor-lifecycle-notices"
            >
              {null}
            </Stack>
            {children}
          </Stack>
        </Container>
      </AppFrame>

      {viewPickerOpen && (
        <Modal onClose={() => setViewPickerOpen(false)} label="Editor view" placement="bottom">
          <ModalHeader title="View" onClose={() => setViewPickerOpen(false)} />
          <ModalBody>
            <SegmentedControl
              aria-label="Editor view"
              value={view}
              onChange={(next) => {
                onViewChange(next);
                setViewPickerOpen(false);
              }}
              options={viewOptions}
            />
          </ModalBody>
        </Modal>
      )}

      {lifecycleOpen && renderLifecycle && !wideHeader && (
        <Modal onClose={() => setLifecycleOpen(false)} label="Document actions" placement="bottom">
          <ModalHeader title="Document actions" onClose={() => setLifecycleOpen(false)} />
          <ModalBody>
            <Stack gap={3}>{renderLifecycle()}</Stack>
          </ModalBody>
        </Modal>
      )}

      {outlineOpen && (
        <Modal onClose={() => setOutlineOpen(false)} label={outlineLabel} placement="right">
          <ModalHeader onClose={() => setOutlineOpen(false)} />
          <ModalBody>{outline}</ModalBody>
        </Modal>
      )}

      {inspectorOpen && (
        <Modal onClose={() => setInspectorOpen(false)} label={inspectorLabel} placement="right">
          <ModalHeader onClose={() => setInspectorOpen(false)} />
          <ModalBody>{inspector}</ModalBody>
        </Modal>
      )}
    </Stack>
  );
}

export function TemplateEditorReadOnlyBoundary({
  disabled,
  contained = false,
  children,
}: {
  disabled: boolean;
  contained?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <fieldset
      disabled={disabled}
      className={contained ? 'm-0 block h-full min-h-0 min-w-0 overflow-hidden border-0 p-0' : 'contents'}
    >
      {children}
    </fieldset>
  );
}
